import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { CalendarIcon, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { GenerateSupplierQuoteRequestDialog } from "./GenerateSupplierQuoteRequestDialog";

interface RequestSupplierQuoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "single_item" | "full_itinerary";
  leadId: string;
  itemId?: string;
}

const FULL_ITINERARY_CATEGORIES = [
  "Transport",
  "Vehicle Provider", 
  "Guide",
  "Tour guides",
  "DMC",
  "Tour operators"
];

export function RequestSupplierQuoteDialog({
  open,
  onOpenChange,
  mode,
  leadId,
  itemId,
}: RequestSupplierQuoteDialogProps) {
  const queryClient = useQueryClient();
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState<Date>();
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [generatedRequests, setGeneratedRequests] = useState<any[]>([]);

  // Fetch lead details
  const { data: lead } = useQuery({
    queryKey: ["lead", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .eq("id", leadId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Fetch itinerary item if single_item mode
  const { data: item } = useQuery({
    queryKey: ["itinerary-item", itemId],
    queryFn: async () => {
      if (!itemId) return null;
      const { data, error } = await supabase
        .from("itinerary_items")
        .select("*")
        .eq("id", itemId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: open && mode === "single_item" && !!itemId,
  });

  // Fetch all itinerary items if full_itinerary mode
  const { data: allItems } = useQuery({
    queryKey: ["itinerary-items", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("itinerary_items")
        .select("*")
        .eq("lead_id", leadId)
        .order("date", { ascending: true, nullsFirst: false })
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: open && mode === "full_itinerary",
  });

  // Fetch suppliers with category filtering
  const { data: suppliers } = useQuery({
    queryKey: ["suppliers-for-quote", mode, item?.item_type],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*, supplier_categories(name)")
        .order("name");
      
      if (error) throw error;

      // Filter based on mode
      if (mode === "full_itinerary") {
        return data.filter((supplier) => {
          const categoryName = supplier.supplier_categories?.name;
          return categoryName && FULL_ITINERARY_CATEGORIES.includes(categoryName);
        });
      } else if (mode === "single_item" && item) {
        // Filter by item type for single items
        return data.filter((supplier) => {
          const categoryName = supplier.supplier_categories?.name;
          if (!categoryName) return false;
          
          // Map item types to relevant categories
          if (item.item_type === "accommodation") {
            return ["Hotels", "Guesthouses", "Lodges", "Accommodation", "Villas"].includes(categoryName);
          }
          if (item.item_type === "transfer") {
            return ["Transport", "Vehicle Provider"].includes(categoryName);
          }
          if (item.item_type === "activity" || item.item_type === "tour") {
            return ["Activity", "Tour", "Tour operators", "Tours"].includes(categoryName);
          }
          if (item.item_type === "meal") {
            return ["Restaurants", "Catering", "Meal"].includes(categoryName);
          }
          return true;
        });
      }
      return data;
    },
    enabled: open,
  });

  const createQuoteRequestsMutation = useMutation({
    mutationFn: async () => {
      if (selectedSuppliers.length === 0) {
        throw new Error("Please select at least one supplier");
      }
      if (!expiresAt) {
        throw new Error("Please set a deadline");
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Prepare itinerary snapshot for full_itinerary mode
      let itinerarySnapshot = null;
      if (mode === "full_itinerary" && allItems) {
        itinerarySnapshot = allItems;
      }

      // Create quote requests for each selected supplier
      const requests = selectedSuppliers.map((supplierId) => ({
        lead_id: leadId,
        request_type: mode,
        itinerary_item_id: mode === "single_item" ? itemId : null,
        supplier_id: supplierId,
        requested_by: user.id,
        notes_to_supplier: notes || null,
        expires_at: expiresAt.toISOString(),
        itinerary_snapshot: mode === "full_itinerary" ? itinerarySnapshot : null,
        status: "sent" as const,
        generated_at: new Date().toISOString(),
      }));

      const { data, error } = await supabase
        .from("supplier_quote_requests")
        .insert(requests)
        .select(`
          *,
          leads(*),
          suppliers(*),
          itinerary_items(*),
          profiles:requested_by(*)
        `);

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setGeneratedRequests(data);
      setPreviewMode(true);
      queryClient.invalidateQueries({ queryKey: ["supplier-quote-requests"] });
      toast.success(`Quote request${data.length > 1 ? 's' : ''} generated for ${data.length} supplier${data.length > 1 ? 's' : ''}`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await createQuoteRequestsMutation.mutateAsync();
  };

  const handleClose = () => {
    setSelectedSuppliers([]);
    setExpiresAt(undefined);
    setNotes("");
    setIsSubmitting(false);
    setPreviewMode(false);
    setGeneratedRequests([]);
    onOpenChange(false);
  };

  const toggleSupplier = (supplierId: string) => {
    setSelectedSuppliers((prev) =>
      prev.includes(supplierId)
        ? prev.filter((id) => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  // Fetch brand logo
  const { data: brandData } = useQuery({
    queryKey: ["brand", lead?.brand_id],
    queryFn: async () => {
      if (!lead?.brand_id) return null;
      const { data } = await supabase
        .from("business_brands")
        .select("logo_url")
        .eq("id", lead.brand_id)
        .single();
      return data;
    },
    enabled: open && !!lead?.brand_id,
  });

  return (
    <>
      <Dialog open={open && !previewMode} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {mode === "single_item" ? "Request Quote for Item" : "Request Full Itinerary Quote"}
            </DialogTitle>
            <DialogDescription>
              {mode === "single_item"
                ? "Select suppliers to request quotes for this specific itinerary item."
                : "Select transport providers, guides, or DMCs to quote on the entire itinerary."}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Item/Itinerary Preview */}
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <h3 className="font-semibold text-sm">
                  {mode === "single_item" ? "Item Details" : "Itinerary Overview"}
                </h3>
                {mode === "single_item" && item && (
                  <div className="space-y-1 text-sm">
                    <p><strong>Title:</strong> {item.title}</p>
                    <p><strong>Type:</strong> {item.item_type}</p>
                    {item.date && <p><strong>Date:</strong> {format(new Date(item.date), "MMM d, yyyy")}</p>}
                    {item.location && <p><strong>Location:</strong> {item.location}</p>}
                  </div>
                )}
                {mode === "full_itinerary" && lead && (
                  <div className="space-y-1 text-sm">
                    <p><strong>Lead:</strong> {lead.title}</p>
                    <p><strong>Destination:</strong> {lead.destination || "N/A"}</p>
                    {lead.start_date && lead.end_date && (
                      <p><strong>Dates:</strong> {format(new Date(lead.start_date), "MMM d")} - {format(new Date(lead.end_date), "MMM d, yyyy")}</p>
                    )}
                    <p><strong>Pax:</strong> {lead.pax_count || 1}</p>
                    <p><strong>Total Items:</strong> {allItems?.length || 0}</p>
                  </div>
                )}
              </div>

              {/* Supplier Selection */}
              <div className="space-y-3">
                <Label>Select Suppliers</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                  {suppliers && suppliers.length > 0 ? (
                    suppliers.map((supplier) => (
                      <div key={supplier.id} className="flex items-start space-x-3">
                        <Checkbox
                          id={supplier.id}
                          checked={selectedSuppliers.includes(supplier.id)}
                          onCheckedChange={() => toggleSupplier(supplier.id)}
                        />
                        <label
                          htmlFor={supplier.id}
                          className="flex-1 text-sm leading-none cursor-pointer"
                        >
                          <div className="font-medium">{supplier.name}</div>
                          {supplier.supplier_categories && (
                            <Badge variant="outline" className="mt-1">
                              {supplier.supplier_categories.name}
                            </Badge>
                          )}
                          {supplier.territory && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {supplier.territory}
                            </div>
                          )}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No suppliers found</p>
                  )}
                </div>
                {selectedSuppliers.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {selectedSuppliers.length} supplier{selectedSuppliers.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              <Separator />

              {/* Deadline */}
              <div className="space-y-2">
                <Label>Quote Deadline *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !expiresAt && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {expiresAt ? format(expiresAt, "PPP") : "Select deadline"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={expiresAt}
                      onSelect={setExpiresAt}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes to Supplier (Optional)</Label>
                <Textarea
                  placeholder={
                    mode === "full_itinerary"
                      ? "E.g., Please quote for full ground transportation including airport transfers and daily excursions..."
                      : "E.g., Need quote for executive room, breakfast included..."
                  }
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Quote Request{selectedSuppliers.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {previewMode && generatedRequests.length > 0 && (
        <GenerateSupplierQuoteRequestDialog
          open={previewMode}
          onOpenChange={(open) => {
            if (!open) handleClose();
          }}
          quoteRequest={generatedRequests[0]}
          brandLogo={brandData?.logo_url}
        />
      )}
    </>
  );
}
